/* About Build section — <about-build-carousel> makes the native scroll-snap
   card track draggable with a mouse. Touch swipe and trackpad two-finger
   scroll already work from the CSS alone (overflow-x: auto); this only adds
   click-and-hold drag support for mouse pointers, which have no native
   drag-to-scroll behavior, plus a manual snap-to-nearest-card on release. */

(function () {
  'use strict';

  if (customElements.get('about-build-carousel')) return;

  const DRAG_THRESHOLD = 3;

  class AboutBuildCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-about-build-track]');
      if (!this.track) return;

      this.isDragging = false;
      this.dragged = false;
      this.startX = 0;
      this.startScrollLeft = 0;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.resetScroll = this.resetScroll.bind(this);

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onClickCapture, true);

      /* Force the row to always start at its leftmost position (left gutter
         visible, peek on the right) — lazy-loaded card images/fonts settling
         in after paint can otherwise nudge the browser's scroll-anchoring and
         leave the track pre-scrolled. */
      this.resetScroll();
      requestAnimationFrame(this.resetScroll);
      window.addEventListener('load', this.resetScroll);
    }

    disconnectedCallback() {
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('click', this.onClickCapture, true);
      window.removeEventListener('load', this.resetScroll);
    }

    resetScroll() {
      this.track.scrollLeft = 0;
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isDragging = true;
      this.dragged = false;
      this.startX = event.clientX;
      this.startScrollLeft = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
      this.track.classList.add('is-dragging');
    }

    onPointerMove(event) {
      if (!this.isDragging) return;

      const delta = event.clientX - this.startX;
      if (Math.abs(delta) > DRAG_THRESHOLD) this.dragged = true;
      this.track.scrollLeft = this.startScrollLeft - delta;
    }

    onPointerUp(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
      if (this.dragged) this.snapToNearestCard();
    }

    onClickCapture(event) {
      if (!this.dragged) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragged = false;
    }

    snapToNearestCard() {
      const cards = this.track.querySelectorAll('.about-build__card');
      if (!cards.length) return;

      const trackLeft = this.track.getBoundingClientRect().left;
      let closest = cards[0];
      let closestDistance = Infinity;

      cards.forEach((card) => {
        const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = card;
        }
      });

      closest.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }

  customElements.define('about-build-carousel', AboutBuildCarousel);
})();
